import { IContentAvatar } from '@/components/ui/Titles/SectionTitle';
import ComplexTooltip from '@/components/ui/Tooltip/ComplexTooltip';
import Link from '@/components/ui/Link';
import type {
  BasicContentRowContainerBlock as BasicContentRowContainerBlockProps,
  TooltipBlock
} from '@/@types/content';
import {
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
  useTransition
} from 'react';
import cardServiceEndpoints, { useGetCardQuery } from '@/redux/api/client/card';
import Icon from '@/components/ui/Icon';
import { IContent } from '@/@types/cms';
import { ISearchResultItem } from '@/@types/client-api/models/ISearchResultItem';
import { IFacetResults, ItemAnalyticsModel } from '@/@types/client-api';
import { aggregateUserData } from '../../ui/SearchResults/Snippets/helpers';
import { SearchableType } from '../../ui/SearchResults/searchableTypeToSnippetVariant';
import { useWalkthrough } from '@/components/blocks/WalkthroughExperienceModalBlock.tsx';
import { trackViewItemListEvent } from '@/analytics/ecommerce';
import { AnalyticsContext } from '@/analytics/constants';
import {
  BasicContentRowBlockType,
  ContainedCategory,
  DataFilter,
  facetGroupsToCheck,
  facultyParams,
  FilterBlockExtended,
  getFeedIdentifier,
  IContentWithCode,
  TitleSize
} from './util';
import BasicContentRow, { BasicContentRowFilter } from './BasicContentRow';
import Block from '@/components/cms/Block';
import ContentArea from '@/components/cms/ContentArea';
import withPliErrorBoundary from '../ErrorBoundary/withPliErrorBoundary';
import { LIBRARY_FILTER_GROUPS, LIBRARY_FILTERS } from '@/utils/libraryConstants';
import { ISearchResultUserInfo } from '@/@types/client-api/models/ISearchResultUserInfo';
import { useDispatch } from 'react-redux';
import { AppDispatch } from '@/redux/store';

export interface BasicContainerBlockProps extends BasicContentRowContainerBlockProps {
  overrideCategory?: string | number | undefined;
  followedFacultyImages?: { thumbnailUrl: string; name: string }[];
  overrideContainedCategories?: ContainedCategory[];
  overrideCollectionUrl?: string;
  overrideHeading?: string;
  walkthroughSteps?: Array<IContent & { applyFor?: string }>;
  orderConfirmationYouMayAlsoLikeProductCode?: string;
  context?: AnalyticsContext;
}

interface CarouselViewport {
  first: number;
  last: number;
}

const _BasicContentRowContainerBlock: React.FC<BasicContainerBlockProps> = ({
  contentLink,
  type,
  enumerateCards = false,
  heading: propsHeading,
  subheading,
  icon,
  tooltip,
  seeAllLink,
  feeds: blockFeeds,
  findMoreLabel,
  avatarImageList,
  overrideCategory,
  displayContentAsList = false,
  followedFacultyImages,
  overrideContainedCategories,
  overrideCollectionUrl,
  fillFeedDynamically,
  format,
  overrideHeading,
  walkthroughSteps,
  fallbackBlock,
  orderConfirmationYouMayAlsoLikeProductCode,
  context,
  name
}) => {
  const walkthroughtContext = useWalkthrough();
  const dispatchWalkthrought = walkthroughtContext?.dispatch;
  const dispatch = useDispatch<AppDispatch>();

  const [loading, setLoading] = useState(true);
  const [firstLoad, setFirstLoad] = useState(true);
  const [searchResultsItems, setSearchResultsItems] = useState<Record<string, unknown>[]>([]);
  const [shouldFetchFilter, setShouldFetchFilter] = useState(false);
  const [selectedFilter, setSelectedFilter] = useState<FilterBlockExtended>({});
  const [shouldFetchOverride, setShouldFetchOverride] = useState(false);
  const [overrideUrl, setOverrideUrl] = useState<string | undefined>(undefined);
  const [shouldFetchPositiveOverrideSearch, setShouldFetchPositiveOverrideSearch] = useState(false);
  const [positiveOverrideSearchUrl, setPositiveOverrideSearchUrl] = useState<string | undefined>(
    undefined
  );
  const [overridePositiveCodes, setOverridePositiveCodes] = useState<string[]>([]);
  const [overrideNegativeCodes, setOverrideNegativeCodes] = useState<string[]>([]);
  const [isInView, setIsInView] = useState(false);
  const [hiddenFeeds, setHiddenFeeds] = useState<string[]>([]);
  const [feedAvailability, setFeedAvailability] = useState<Record<string, boolean>>({});

  const [, startTransition] = useTransition();

  const carouselViewportRef = useRef<CarouselViewport>({} as CarouselViewport);
  const lastTrackedFilterKeyRef = useRef<string>('');
  const suppressSwipeTrackingRef = useRef<boolean>(false);

  const heading = propsHeading?.replace('{content-name}', overrideHeading ?? '');
  const avatarImagesLimit = 2;

  const blockRef = useRef<HTMLDivElement | null>(null);
  const [hasScrolled, setHasScrolled] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      setHasScrolled(true);
      window.removeEventListener('scroll', handleScroll);
    };

    window.addEventListener('scroll', handleScroll);

    return () => {
      window.removeEventListener('scroll', handleScroll);
    };
  }, []);

  const isLibraryBlock = type === BasicContentRowBlockType.Library;

  const {
    currentData: dataFilter,
    refetch: refetchFilter,
    isLoading: isLoadingFilter,
    isFetching: isFetchingFilter,
    isUninitialized: isUninitializedFilter,
    isError: isCardError
  } = useGetCardQuery(selectedFilter.value as string, {
    skip: !shouldFetchFilter || !isInView || !selectedFilter.value,
    refetchOnMountOrArgChange: isLibraryBlock ? 30 : false
  });

  const {
    currentData: dataOverride,
    refetch: refetchOverride,
    isLoading: isLoadingOverride,
    isFetching: isFetchingOverride,
    isUninitialized: isUninitializedOverride,
    isError: isOverrideCardError
  } = useGetCardQuery(overrideUrl as string, {
    skip: !shouldFetchOverride || !isInView || !overrideUrl,
    refetchOnMountOrArgChange: isLibraryBlock ? 30 : false
  });

  const {
    currentData: dataPositiveOverrideSearch,
    refetch: refetchPositiveOverrideSearch,
    isLoading: isLoadingPositiveOverrideSearch,
    isFetching: isFetchingPositiveOverrideSearch,
    isUninitialized: isUninitializedPositiveOverrideSearch,
    isError: isPositiveOverrideSearchCardError
  } = useGetCardQuery(positiveOverrideSearchUrl as string, {
    skip: !shouldFetchPositiveOverrideSearch || !isInView || !positiveOverrideSearchUrl
  });

  const isError = useMemo(
    () => !!isCardError || !!isOverrideCardError || !!isPositiveOverrideSearchCardError,
    [isCardError, isOverrideCardError, isPositiveOverrideSearchCardError]
  );

  const feedHasFacetItems = (feedUrl?: string, facetResults?: IFacetResults) => {
    if (feedUrl) {
      const searchParams = new URLSearchParams(new URL(feedUrl, window.location.origin).search);
      for (const facetGroupName of facetGroupsToCheck) {
        if (searchParams.has(facetGroupName)) {
          const facetValue = searchParams.get(facetGroupName);
          const facet = facetResults?.facetGroups
            ?.find(fg => fg.groupTypeId == facetGroupName)
            ?.facets?.find(f => f.value == facetValue);

          return !(facet && facet.count == 0);
        }
      }
    }

    return true;
  };

  const hasEmptyFacultyQueryParam = (feedUrl?: string) => {
    if (feedUrl) {
      const searchParams = new URLSearchParams(new URL(feedUrl, window.location.origin).search);
      return facultyParams.some(param => searchParams.has(param) && !searchParams.get(param));
    }

    return false;
  };

  const feeds = useMemo(
    () => {
      if (
        fillFeedDynamically &&
        overrideContainedCategories &&
        overrideContainedCategories.length
      ) {
        return overrideContainedCategories.map(category => {
          const modifiedCollectionUrl = overrideCollectionUrl?.replace(
            /\[id\]/i,
            category.id as string
          );

          return {
            text: category.name || '',
            value: modifiedCollectionUrl || ''
          };
        });
      }

      if (orderConfirmationYouMayAlsoLikeProductCode) {
        return blockFeeds?.map(blockFeed => ({
          ...blockFeed,
          value: blockFeed.value?.replace(
            '{ProductCode}',
            orderConfirmationYouMayAlsoLikeProductCode
          )
        }));
      } else {
        return blockFeeds
          ?.filter(blockFeed => type !== 'faculty' || !hasEmptyFacultyQueryParam(blockFeed?.value))
          ?.filter(blockFeed =>
            feedHasFacetItems(blockFeed?.value, (dataFilter as unknown as DataFilter)?.facetResults)
          ) as FilterBlockExtended[];
      }
    },
    // TODO: tech-debt: document which dependencies are intentionally left out and why; so other needed dependencies can be safely added
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [
      fillFeedDynamically,
      overrideContainedCategories,
      overrideCollectionUrl,
      blockFeeds,
      isLoadingFilter,
      isUninitializedFilter
    ]
  );

  let dataTooltip: React.ComponentProps<typeof ComplexTooltip> | undefined = undefined;

  const getCodes = useCallback((codes: IContent[] | undefined, asArray = false) => {
    if (!codes) {
      return undefined;
    }

    const filteredCodes = codes
      .filter(override => (override.contentLink?.expanded as IContentWithCode).code)
      .map(override => {
        const code = (override.contentLink?.expanded as IContentWithCode).code;
        const parentProductCode = (override.contentLink?.expanded as IContentWithCode)
          .parentProductCode;
        return parentProductCode ? `${parentProductCode}|${code}` : code;
      });

    if (asArray) {
      return filteredCodes;
    } else {
      return filteredCodes.length > 0 ? filteredCodes.join(';') : undefined;
    }
  }, []);

  const prepareFeedRequestData = useCallback(
    (feed: FilterBlockExtended, pageSizeOverride?: number) => {
      const modifiedFilter = { ...feed };

      const {
        value,
        editorOverridesPositive,
        editorOverridesPositiveSearchValue,
        editorOverridesNegative,
        editorExclusions,
        maxNumberOfItems
      } = feed;

      const editorOverridesPositiveCodes = (getCodes(editorOverridesPositive, true) ?? []) as string[];
      const editorOverridesNegativeCodes = (getCodes(editorOverridesNegative, true) ?? []) as string[];
      const mergedOverrideCodes = [
        ...editorOverridesPositiveCodes,
        ...editorOverridesNegativeCodes
      ];
      const editorOverrideCodes = mergedOverrideCodes.join(';');

      let positiveOverrideSearchUrl = editorOverridesPositiveSearchValue;
      const pageSize = pageSizeOverride ?? maxNumberOfItems;

      if (positiveOverrideSearchUrl) {
        if (overrideCategory && positiveOverrideSearchUrl.toLowerCase().includes('[id]')) {
          positiveOverrideSearchUrl = positiveOverrideSearchUrl.replace(
            /\[id\]/i,
            overrideCategory as string
          );
        }

        const positiveOverrideSearchParams = new URLSearchParams(
          new URL(positiveOverrideSearchUrl, window.location.origin).search
        );

        if (!positiveOverrideSearchParams.has('PageSize') && pageSize) {
          positiveOverrideSearchUrl += `${positiveOverrideSearchUrl.includes('?') ? '&' : '?'}PageSize=${pageSize}`;
        }
      }

      if (overrideCategory && value && value.toLowerCase().includes('[id]')) {
        modifiedFilter.value = value.replace(/\[id\]/i, overrideCategory as string);
      }

      if (value) {
        const editorExclusionCodes = getCodes(editorExclusions);
        let modifiedFilterValue = modifiedFilter.value;

        if (editorExclusionCodes) {
          const queryString = `ExcludeCode=${editorExclusionCodes}`;

          if (modifiedFilterValue?.includes('?')) {
            modifiedFilterValue += `&${queryString}`;
          } else {
            modifiedFilterValue += `?${queryString}`;
          }
        }

        const searchParams = new URLSearchParams(
          new URL(modifiedFilterValue ?? value, window.location.origin).search
        );

        if (!searchParams.has('PageSize') && pageSize) {
          modifiedFilterValue += `${modifiedFilterValue!.includes('?') ? '&' : '?'}PageSize=${pageSize}`;
        }

        modifiedFilter.value = modifiedFilterValue;
      }

      return {
        modifiedFilter,
        overridePositiveCodes: editorOverridesPositiveCodes,
        overrideNegativeCodes: editorOverridesNegativeCodes,
        overrideUrl: editorOverrideCodes
          ? `/search/product-code?ProductCodes=${editorOverrideCodes}`
          : undefined,
        positiveOverrideSearchUrl,
        hasFetchableSource: !!(
          editorOverrideCodes ||
          modifiedFilter.value ||
          positiveOverrideSearchUrl
        )
      };
    },
    [getCodes, overrideCategory]
  );

  const runCardRequest = useCallback(
    async (url?: string) => {
      if (!url) {
        return { ok: true, data: undefined as unknown };
      }

      const action = dispatch(
        cardServiceEndpoints.endpoints.getCard.initiate(url, {
          forceRefetch: true,
          subscribe: false
        })
      );

      try {
        return { ok: true, data: await action.unwrap() };
      } catch {
        return { ok: false, data: undefined as unknown };
      }
    },
    [dispatch]
  );

  const checkFeedHasData = useCallback(
    async (feed: FilterBlockExtended) => {
      const {
        modifiedFilter,
        overridePositiveCodes: positiveCodes,
        overrideNegativeCodes: negativeCodes,
        overrideUrl,
        positiveOverrideSearchUrl,
        hasFetchableSource
      } = prepareFeedRequestData(feed, 1);

      if (!hasFetchableSource) {
        return false;
      }

      const [overrideResponse, positiveOverrideSearchResponse, filterResponse] = await Promise.all([
        runCardRequest(overrideUrl),
        runCardRequest(positiveOverrideSearchUrl),
        runCardRequest(modifiedFilter.value)
      ]);

      if (!overrideResponse.ok || !positiveOverrideSearchResponse.ok || !filterResponse.ok) {
        return true;
      }

      const overrideData = (overrideResponse.data as IContentWithCode[]) || [];
      const positiveOverrideSearchData =
        (positiveOverrideSearchResponse.data as DataFilter | undefined)?.searchResults
          ?.searchResultsItems || ([] as Record<string, unknown>[]);
      const positiveOverrideProductCodes = new Set([
        ...positiveCodes.flatMap(code => code.split('|')),
        ...positiveOverrideSearchData
          .map(item => (item as IContentWithCode).productCode)
          .filter((code): code is string => Boolean(code))
      ]);
      const negativeOverrideProductCodes = new Set(negativeCodes.flatMap(code => code.split('|')));
      const overridePositiveData = overrideData.filter(override =>
        positiveOverrideProductCodes.has(override.productCode as string)
      );
      const overrideNegativeData = overrideData.filter(override =>
        negativeOverrideProductCodes.has(override.productCode as string)
      );
      const searchResultsItemsFeed = (filterResponse.data as DataFilter | undefined)?.searchResults
        ?.searchResultsItems;
      const filteredSearchResultsItemsFeed = searchResultsItemsFeed?.filter(
        (item: IContentWithCode) => {
          const productCode = item.productCode as string | undefined;

          if (!productCode) {
            return true;
          }

          return (
            !positiveOverrideProductCodes.has(productCode) &&
            !negativeOverrideProductCodes.has(productCode)
          );
        }
      );

      return Boolean(
        overridePositiveData.length ||
          positiveOverrideSearchData.length ||
          filteredSearchResultsItemsFeed?.length ||
          overrideNegativeData.length
      );
    },
    [prepareFeedRequestData, runCardRequest]
  );

  const fetchFeed = useCallback(
    (feed: FilterBlockExtended) => {
      const {
        modifiedFilter,
        overridePositiveCodes: editorOverridesPositiveCodes,
        overrideNegativeCodes: editorOverridesNegativeCodes,
        overrideUrl,
        positiveOverrideSearchUrl,
        hasFetchableSource
      } = prepareFeedRequestData(feed);

      setOverridePositiveCodes([]);
      setOverrideNegativeCodes([]);
      setPositiveOverrideSearchUrl(undefined);

      setOverridePositiveCodes(editorOverridesPositiveCodes as string[]);
      setOverrideNegativeCodes(editorOverridesNegativeCodes as string[]);

      // If feed has no fetchable source, it has no items
      if (!hasFetchableSource) {
        setSearchResultsItems([]);
        setLoading(false);
        setFirstLoad(false);
        if (
          modifiedFilter.value !== selectedFilter.value ||
          modifiedFilter.text !== selectedFilter.text
        ) {
          setSelectedFilter(modifiedFilter);
        }
        return;
      }

      if (overrideUrl) {
        setOverrideUrl(overrideUrl);
        if (isUninitializedOverride) {
          setShouldFetchOverride(true);
        } else {
          startTransition(() => {
            refetchOverride();
          });
        }
      }

      if (positiveOverrideSearchUrl) {
        setPositiveOverrideSearchUrl(positiveOverrideSearchUrl);

        if (isUninitializedPositiveOverrideSearch) {
          setShouldFetchPositiveOverrideSearch(true);
        } else {
          startTransition(() => {
            refetchPositiveOverrideSearch();
          });
        }
      }

      if (modifiedFilter.value) {
        if (isUninitializedFilter) {
          setShouldFetchFilter(true);
        } else {
          startTransition(() => {
            refetchFilter();
          });
        }
      }

      // Only update if the filter has actually changed
      if (
        modifiedFilter.value !== selectedFilter.value ||
        modifiedFilter.text !== selectedFilter.text
      ) {
        setSelectedFilter(modifiedFilter);
      }
    },
    // TODO: tech-debt: document which dependencies are intentionally left out and why; so other needed dependencies can be safely added
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [prepareFeedRequestData, refetchFilter, refetchOverride, refetchPositiveOverrideSearch]
  );

  const forceRefetch = useCallback(() => {
    startTransition(() => {
      refetchFilter();
    });
  }, [refetchFilter]);

  const selectedFeedIdentifier = getFeedIdentifier(selectedFilter);
  const fallbackFeedIdentifier = selectedFeedIdentifier || getFeedIdentifier(feeds?.[0] ?? {});
  const shouldValidateFeedAvailability = !isLibraryBlock && (feeds?.length ?? 0) > 1;

  // Visible feeds (not hidden/suppressed)
  const visibleFeeds = useMemo(() => {
    const visFeeds = feeds?.filter(feed => {
      const feedIdentifier = getFeedIdentifier(feed);

      if (hiddenFeeds.find(f => f === feedIdentifier)) {
        return false;
      }

      if (!shouldValidateFeedAvailability) {
        return true;
      }

      if (feedIdentifier === fallbackFeedIdentifier) {
        return true;
      }

      return feedAvailability[feedIdentifier] === true;
    });

    return visFeeds;
  }, [feedAvailability, feeds, fallbackFeedIdentifier, hiddenFeeds, shouldValidateFeedAvailability]);

  useEffect(() => {
    if (!shouldValidateFeedAvailability || !selectedFeedIdentifier || firstLoad || loading) {
      return;
    }

    const hasSelectedFeedData = searchResultsItems.length > 0;

    setFeedAvailability(prev => {
      if (prev[selectedFeedIdentifier] === hasSelectedFeedData) {
        return prev;
      }

      return {
        ...prev,
        [selectedFeedIdentifier]: hasSelectedFeedData
      };
    });
  }, [
    firstLoad,
    loading,
    searchResultsItems.length,
    selectedFeedIdentifier,
    shouldValidateFeedAvailability
  ]);

  useEffect(() => {
    if (!shouldValidateFeedAvailability || !isInView || !feeds?.length) {
      return;
    }

    const feedsToValidate = feeds.filter(feed => {
      const feedIdentifier = getFeedIdentifier(feed);

      return (
        !!feedIdentifier &&
        feedIdentifier !== selectedFeedIdentifier &&
        feedAvailability[feedIdentifier] === undefined &&
        !hiddenFeeds.find(hiddenFeed => hiddenFeed === feedIdentifier)
      );
    });

    if (!feedsToValidate.length) {
      return;
    }

    let isCancelled = false;

    Promise.all(
      feedsToValidate.map(async feed => ({
        feedIdentifier: getFeedIdentifier(feed),
        hasData: await checkFeedHasData(feed)
      }))
    ).then(results => {
      if (isCancelled) {
        return;
      }

      setFeedAvailability(prev => {
        let hasChanges = false;
        const nextAvailability = { ...prev };

        results.forEach(({ feedIdentifier, hasData }) => {
          if (!feedIdentifier || nextAvailability[feedIdentifier] === hasData) {
            return;
          }

          nextAvailability[feedIdentifier] = hasData;
          hasChanges = true;
        });

        return hasChanges ? nextAvailability : prev;
      });
    });

    return () => {
      isCancelled = true;
    };
  }, [
    checkFeedHasData,
    feedAvailability,
    feeds,
    hiddenFeeds,
    isInView,
    selectedFeedIdentifier,
    shouldValidateFeedAvailability
  ]);

  // No results -> update hidden feeds so they can be suppressed from feed list
  const handleNoResults = useCallback(
    (feedIdentifier: string) => {
      setFeedAvailability(prev => {
        if (prev[feedIdentifier] === false) {
          return prev;
        }

        return {
          ...prev,
          [feedIdentifier]: false
        };
      });

      setHiddenFeeds(prev => {
        if (!prev.find(f => f === feedIdentifier)) {
          return [...prev, feedIdentifier];
        }

        return prev;
      });

      if (selectedFeedIdentifier !== feedIdentifier) {
        return;
      }

      const nextVisibleFeed = visibleFeeds?.find(
        feed => getFeedIdentifier(feed) !== feedIdentifier
      );

      if (nextVisibleFeed) {
        fetchFeed(nextVisibleFeed);
      }
    },
    [fetchFeed, selectedFeedIdentifier, visibleFeeds]
  );

  // Fetch / select from visible feeds
  useLayoutEffect(() => {
    if (!visibleFeeds?.length) return;

    const firstVisibleFeed = visibleFeeds[0];
    const isSelectedFeedVisible = visibleFeeds.some(
      feed => getFeedIdentifier(feed) === selectedFeedIdentifier
    );

    if (!selectedFeedIdentifier || !isSelectedFeedVisible) {
      fetchFeed(firstVisibleFeed);
    }
  }, [visibleFeeds, selectedFeedIdentifier, fetchFeed]);

  useEffect(() => {
    startTransition(() => {
      setLoading(
        isLoadingFilter ||
          isFetchingFilter ||
          isLoadingOverride ||
          isFetchingOverride ||
          isLoadingPositiveOverrideSearch ||
          isFetchingPositiveOverrideSearch
      );

      if (!dataFilter && !dataOverride && !dataPositiveOverrideSearch) return;

      const overridePositiveSearchData =
        (dataPositiveOverrideSearch as unknown as DataFilter)?.searchResults?.searchResultsItems ||
        ([] as Record<string, unknown>[]);

      const positiveOverrideProductCodes = new Set([
        ...overridePositiveCodes.flatMap(code => code.split('|')),
        ...overridePositiveSearchData
          .map(item => (item as IContentWithCode).productCode)
          .filter((code): code is string => Boolean(code))
      ]);

      const negativeOverrideProductCodes = new Set(
        overrideNegativeCodes.flatMap(code => code.split('|'))
      );

      const overridePositiveData =
        (dataOverride as unknown as IContentWithCode[])?.filter((override: IContentWithCode) => {
          return positiveOverrideProductCodes.has(override.productCode as string);
        }) || ([] as Record<string, unknown>[]);

      const overrideNegativeData =
        (dataOverride as unknown as IContentWithCode[])?.filter((override: IContentWithCode) => {
          return negativeOverrideProductCodes.has(override.productCode as string);
        }) || ([] as Record<string, unknown>[]);

      const searchResultsItemsFeed = (dataFilter as unknown as DataFilter)?.searchResults
        ?.searchResultsItems;

      const filteredSearchResultsItemsFeed = searchResultsItemsFeed?.filter(
        (item: IContentWithCode) => {
          const productCode = item.productCode as string | undefined;

          if (!productCode) {
            return true;
          }

          return (
            !positiveOverrideProductCodes.has(productCode) &&
            !negativeOverrideProductCodes.has(productCode)
          );
        }
      );

      const dedupeByProductCode = (items: Record<string, unknown>[]) => {
        const seen = new Set<string>();

        return items.filter(item => {
          const productCode = (item as IContentWithCode).productCode;

          if (!productCode) {
            return true;
          }

          if (seen.has(productCode)) {
            return false;
          }

          seen.add(productCode);
          return true;
        });
      };

      const newSearchResultsData = dedupeByProductCode([
        ...((overridePositiveData ?? []) as Record<string, unknown>[]),
        ...((overridePositiveSearchData ?? []) as Record<string, unknown>[]),
        ...((filteredSearchResultsItemsFeed ?? []) as Record<string, unknown>[]),
        ...((overrideNegativeData ?? []) as Record<string, unknown>[])
      ]);

      setSearchResultsItems(newSearchResultsData);
      setFirstLoad(false);
    });
  }, [
    dataFilter,
    isLoadingFilter,
    isFetchingFilter,
    dataOverride,
    dataPositiveOverrideSearch,
    isLoadingOverride,
    isFetchingOverride,
    isLoadingPositiveOverrideSearch,
    isFetchingPositiveOverrideSearch,
    overridePositiveCodes,
    overrideNegativeCodes,
    isInView
  ]);

  if (tooltip && tooltip.length) {
    const {
      title: tooltipTitle,
      copy: tooltipCopy,
      link: tooltipLink
    } = tooltip[0] as TooltipBlock;

    dataTooltip = {
      children: (
        <>
          <ComplexTooltip.Trigger>
            <Icon name="info" size="small" className="text-gray" />
          </ComplexTooltip.Trigger>

          <ComplexTooltip.Content>
            <div>
              {tooltipTitle && <p className="text-2 mb-1 font-bold">{tooltipTitle}</p>}
              {tooltipCopy && <p className="text-2 mb-2">{tooltipCopy}</p>}
              {tooltipLink && (
                <Link
                  href={tooltipLink?.href}
                  text={tooltipLink?.text}
                  title={tooltipLink?.title}
                  target={tooltipLink?.target}
                />
              )}
            </div>
          </ComplexTooltip.Content>
        </>
      )
    };
  }

  const refreshLibraryData = useCallback(() => {
    if (isLibraryBlock) {
      forceRefetch();
    }
  }, [isLibraryBlock, forceRefetch]);

  const handleFilterClick = useCallback(
    (filter: FilterBlockExtended) => {
      suppressSwipeTrackingRef.current = true;
      fetchFeed(filter);
    },
    [fetchFeed]
  );

  const handleClickFindMore = () => {
    document.getElementById('search-overlay-trigger')?.click();
  };

  const getTitleSizeByFormat = (format?: string) => {
    return (format as TitleSize) ?? 'h5';
  };

  const titleSizeByFormat = getTitleSizeByFormat(format);

  const filteredItems = useMemo(() => {
    let newlyFilteredItems =
      (selectedFilter?.hidePurchased
        ? searchResultsItems?.filter(c => {
            const item = c as ISearchResultItem;
            const userList = item?.userList;
            const variant = item?.searchableType?.toLowerCase();
            if (!userList || !variant) {
              return true;
            }

            const userAggregatedData = aggregateUserData({
              userList,
              variant: variant as SearchableType
            });

            return !userAggregatedData?.isPurchased;
          })
        : searchResultsItems) || [];

    const creditsForRequestFilter = LIBRARY_FILTER_GROUPS[
      LIBRARY_FILTERS.CREDIT_STATUS
    ].filters.find(f => f.value === 'creditsForRequest');

    const continueWatchingFilter = LIBRARY_FILTER_GROUPS[
      LIBRARY_FILTERS.LIBRARY_ITEM_STATUS
    ].filters.find(f => f.value === 'continueWatching');

    const getMyLibraryFilterValue = (filterValue?: string): string | null => {
      if (!filterValue) return null;

      try {
        const url = new URL(filterValue, window.location.origin);
        return url.searchParams.get('MyLibrary')?.toLowerCase() || null;
      } catch {
        return null;
      }
    };

    const filterMatches = (selectedValue?: string, selectedText?: string, targetValue?: string) => {
      const targetValueLower = targetValue?.toLowerCase();
      if (!targetValueLower) return false;
      if (selectedValue === targetValueLower || selectedText === targetValueLower) {
        return true;
      }

      const myLibraryValue = getMyLibraryFilterValue(selectedValue);
      return myLibraryValue === targetValueLower;
    };

    if (isLibraryBlock) {
      if (
        filterMatches(selectedFilter?.value, selectedFilter?.text, continueWatchingFilter?.value)
      ) {
        newlyFilteredItems = newlyFilteredItems.filter(item => {
          const itemData = item as ISearchResultItem;
          const userList = itemData?.userList;

          if (!userList || !Array.isArray(userList)) {
            return false;
          }

          return userList.some(
            userItem =>
              (userItem.playbackProgressPct != null &&
                userItem.playbackProgressPct > 0 &&
                userItem.playbackProgressPct < 99) ||
              (userItem.cleProgressPct != null &&
                userItem.cleProgressPct > 0 &&
                userItem.cleProgressPct < 99)
          );
        });
      } else if (
        filterMatches(selectedFilter?.value, selectedFilter?.text, creditsForRequestFilter?.value)
      ) {
        newlyFilteredItems = newlyFilteredItems.filter(item => {
          const itemData = item as ISearchResultItem;
          const userList = itemData?.userList;

          if (!userList || !Array.isArray(userList)) {
            return false;
          }

          const isSegment = itemData.searchableType?.toLowerCase() === 'ondemandsegment';
          const isProgram = itemData.searchableType?.toLowerCase() === 'ondemandprogram';

          if (isSegment && itemData.parentCode) {
            const parentProgram = searchResultsItems?.find(searchItem => {
              const searchItemData = searchItem as ISearchResultItem;
              return searchItemData?.productCode === itemData.parentCode;
            });

            if (parentProgram) {
              const parentData = parentProgram as ISearchResultItem;
              const parentUserList = parentData?.userList;

              const isParentComplete = parentUserList?.some(
                (userItem: ISearchResultUserInfo) =>
                  userItem.cleProgressPct != null && userItem.cleProgressPct >= 100
              );

              if (isParentComplete) {
                return false;
              }
            }
          }

          // For programs: show if 100% complete OR if any segment has credits available
          if (isProgram) {
            const isProgramComplete = userList.some(
              (userItem: ISearchResultUserInfo) =>
                userItem.cleProgressPct != null && userItem.cleProgressPct >= 100
            );

            if (isProgramComplete) {
              return true;
            }

            // Check if program has segments with credits available
            const programSegments = searchResultsItems?.filter(searchItem => {
              const segmentData = searchItem as ISearchResultItem;
              return (
                segmentData?.searchableType?.toLowerCase() === 'ondemandsegment' &&
                segmentData?.parentCode === itemData?.productCode
              );
            });

            const hasSegmentsWithCredits = programSegments?.some(segment => {
              const segmentData = segment as ISearchResultItem;
              return segmentData?.userList?.some(
                (userItem: ISearchResultUserInfo) =>
                  userItem.cleProgressPct != null &&
                  userItem.cleProgressPct > 0 &&
                  userItem.hasCreditsForRequest === true
              );
            });

            if (hasSegmentsWithCredits) {
              return true;
            }
          }

          // For segments and other items: check if they have credits available
          return userList.some(
            (userItem: ISearchResultUserInfo) =>
              userItem.cleProgressPct != null &&
              userItem.cleProgressPct > 0 &&
              userItem.hasCreditsForRequest === true
          );
        });
      }
    }

    if (selectedFilter?.maxNumberOfItems && selectedFilter?.maxNumberOfItems > 0) {
      newlyFilteredItems = newlyFilteredItems.slice(0, selectedFilter?.maxNumberOfItems);
    }

    return newlyFilteredItems;
  }, [selectedFilter, searchResultsItems, isLibraryBlock]);

  const listId = useMemo(() => {
    const contentId = contentLink?.id ?? '';
    const feedIndex = feeds?.findIndex(feed => feed.text === selectedFilter.text) ?? -1;

    if (contentId && feedIndex !== -1) {
      const index = feedIndex + 1;
      return `${contentId}-${index}`;
    }

    return 'BasicContentRow';
  }, [contentLink?.id, feeds, selectedFilter.text]);

  const trackCurrentViewportViewItemList = useCallback(
    (first: number, last: number) => {
      const items = filteredItems
        .slice(first, last + 1)
        .map(item => (item as ISearchResultItem).itemAnalyticsModel)
        .filter((item): item is ItemAnalyticsModel => item !== undefined);

      if (items.length === 0) return;

      const listName = `${heading} - ${selectedFilter.text}` || '';
      trackViewItemListEvent(listId, listName, items, context, first);
    },
    [filteredItems, heading, selectedFilter.text, listId, context]
  );

  const setCarouselViewport = useCallback(
    (first: number, last: number, isSwiped?: boolean) => {
      carouselViewportRef.current = { first, last };

      if (isSwiped && filteredItems.length > 0 && !suppressSwipeTrackingRef.current) {
        trackCurrentViewportViewItemList(first, last);
      }
    },
    [filteredItems.length, trackCurrentViewportViewItemList]
  );

  useEffect(() => {
    const targetElement = blockRef.current;

    if (!targetElement) return;

    const observer = new IntersectionObserver(
      entries => {
        entries.forEach(entry => {
          if (entry.isIntersecting) {
            setIsInView(true);
          }
        });
      },
      { threshold: 0.1 }
    );
    observer.observe(targetElement);

    return () => {
      observer.unobserve(targetElement);
    };
  }, [carouselViewportRef, feeds]);

  useEffect(() => {
    if (!isInView || !hasScrolled || filteredItems.length === 0) return;

    const filterKey = `${selectedFilter.value ?? ''}::${selectedFilter.text ?? ''}`;
    if (lastTrackedFilterKeyRef.current === filterKey) return;

    const first =
      typeof carouselViewportRef.current.first === 'number' ? carouselViewportRef.current.first : 0;
    const defaultLast = Math.max(filteredItems.length - 1, 0);
    const last =
      typeof carouselViewportRef.current.last === 'number'
        ? carouselViewportRef.current.last
        : defaultLast;

    trackCurrentViewportViewItemList(first, last);
    lastTrackedFilterKeyRef.current = filterKey;
    suppressSwipeTrackingRef.current = false;
  }, [
    isInView,
    hasScrolled,
    filteredItems,
    selectedFilter.value,
    selectedFilter.text,
    trackCurrentViewportViewItemList
  ]);

  const avatarImages: IContentAvatar[] | undefined = useMemo(() => {
    let avatars: IContentAvatar[] | undefined;
    if (followedFacultyImages && followedFacultyImages.length > 0) {
      avatars = followedFacultyImages.map(image => ({
        fullName: '',
        name: image.name,
        image: {
          url: image.thumbnailUrl
        },
        icon: image ? undefined : 'profile'
      }));

      // Only render avatar images whose works appear in the
      const authorsFromSearchResults = filteredItems
        .flatMap((item: ISearchResultItem) => item['authors']?.map(a => a.keyword))
        .filter(name => name != null);
      if (authorsFromSearchResults.length > 0) {
        avatars = avatars?.filter(a => authorsFromSearchResults.includes(a.name ?? ''));
      }
    } else if (avatarImageList && avatarImageList.length > 0) {
      avatars = avatarImageList as IContentAvatar[];
    }

    return avatars?.slice(0, avatarImagesLimit);
  }, [avatarImageList, followedFacultyImages, filteredItems]);

  const [firstLoadLibrary, setFirstLoadLibrary] = useState<boolean>(false);

  const contentRow = useMemo(() => {
    const feedIndex = feeds?.findIndex(feed => feed.text === selectedFilter.text) ?? 0;
    const itemListAnalyticsModel = {
      listId: `${contentLink?.id?.toString()}-${feedIndex + 1}`,
      listName: `${name} - ${selectedFilter.text}`
    };

    if (!visibleFeeds?.length) return <></>;

    return (
      <Block
        contentLinkID={contentLink?.id}
        className="module-spacing w-full overflow-x-clip"
        data-component={BasicContentRowContainerBlock.name}
      >
        <BasicContentRow
          isLoading={loading}
          isFirstLoad={firstLoad}
          isLoadingFilter={!!isLoadingFilter || !!isFetchingFilter}
          displayContentAsList={displayContentAsList}
          type={type}
          findMoreLabel={findMoreLabel}
          seeAllLink={seeAllLink}
          searchResultsItems={searchResultsItems}
          filteredItems={filteredItems}
          ref={blockRef}
          onNoResults={feeds && feeds.length > 1 && !isLibraryBlock ? handleNoResults : undefined}
          filterProps={{
            filters: visibleFeeds,
            showSingleVisibleFilter: (feeds?.length ?? 0) > 1,
            handleFilterClick: (filter: BasicContentRowFilter) => {
              if (filter.text !== selectedFilter?.text) handleFilterClick(filter);
            },
            selectedFilter: selectedFilter
          }}
          sectionTitle={{
            title: heading || '',
            subcopy: subheading,
            tooltip: dataTooltip,
            tooltipWalkthrough:
              type == BasicContentRowBlockType.Library && walkthroughSteps
                ? {
                    index: walkthroughSteps.findIndex(step => step.applyFor === 'library'),
                    walkthroughSteps: walkthroughSteps.length,
                    ...walkthroughSteps.find(step => step.applyFor === 'library')
                  }
                : undefined,
            titleSize: titleSizeByFormat,
            icon: icon ? { name: icon } : undefined,
            avatars: avatarImages
          }}
          productCarouselProps={{
            type: type as BasicContentRowBlockType,
            cards: filteredItems,
            labelFindMore: findMoreLabel || '',
            onClickFindMore: handleClickFindMore,
            onRefreshParentData: refreshLibraryData,
            isLoading: loading,
            enumerateCards: enumerateCards,
            SetCarouselViewport: setCarouselViewport,
            listId: listId,
            listName: `${heading} - ${selectedFilter.text}` || '',
            itemListAnalyticsModel: itemListAnalyticsModel
          }}
        />
      </Block>
    );
  }, [
    feeds,
    contentLink?.id,
    name,
    selectedFilter,
    loading,
    firstLoad,
    isLoadingFilter,
    isFetchingFilter,
    displayContentAsList,
    type,
    findMoreLabel,
    seeAllLink,
    searchResultsItems,
    filteredItems,
    handleNoResults,
    visibleFeeds,
    heading,
    subheading,
    dataTooltip,
    walkthroughSteps,
    titleSizeByFormat,
    icon,
    avatarImages,
    refreshLibraryData,
    enumerateCards,
    setCarouselViewport,
    listId,
    handleFilterClick,
    isLibraryBlock
  ]);

  useEffect(() => {
    if (
      !feeds ||
      feeds.length === 0 ||
      (feeds.length === 1 && !loading && !searchResultsItems.length && isInView)
    ) {
      if (
        type === 'library' &&
        !firstLoadLibrary &&
        !sessionStorage.getItem('lightboxModalSessionDismissed')
      ) {
        setFirstLoadLibrary(true);
        dispatchWalkthrought?.({ type: 'DELETE_STEP', applyFor: 'library' });
      }
    }
  }, [
    dispatchWalkthrought,
    feeds,
    firstLoadLibrary,
    isInView,
    loading,
    searchResultsItems.length,
    type
  ]);
  if (
    !feeds ||
    feeds.length === 0 ||
    (feeds.length === 1 && !loading && !searchResultsItems.length && isInView)
  ) {
    return null;
  }

  return isError && type == BasicContentRowBlockType.Library ? (
    <ContentArea components={fallbackBlock} />
  ) : (
    contentRow
  );
};

const BasicContentRowContainerBlock = withPliErrorBoundary(_BasicContentRowContainerBlock);
export default BasicContentRowContainerBlock;
